/* ============================================================
   PebbleBloom - UGC Video Wall
   File: assets/pb-ugc-videos.js

   Tap to play with sound. Only one clip plays at a time - starting
   a second pauses the first, because two videos talking over each
   other is the fastest way to lose a visitor.

   Muted preview on hover is desktop-only and gated on a real
   pointer, so a touch device never triggers it on scroll.
   ============================================================ */

(function () {
  'use strict';

  var canHover =
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var current = null;

  function stopCurrent(except) {
    if (!current || current === except) return;
    try {
      current.pause();
      current.muted = true;
      current.currentTime = 0;
    } catch (e) {
      /* a detached node can throw; nothing to recover */
    }
    var f = current.closest('[data-pb-ugc-item]');
    if (f) f.classList.remove('is-playing');
    current = null;
  }

  function wire(frame) {
    if (frame.dataset.pbUgcInit === '1') return;
    frame.dataset.pbUgcInit = '1';

    var video = frame.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.setAttribute('playsinline', '');

    function play(withSound) {
      stopCurrent(video);
      video.muted = !withSound;
      var p = video.play();
      // Autoplay can reject (policy, or the tab is backgrounded).
      // Fall back to muted rather than leaving a dead play button.
      if (p && p.catch) {
        p.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
      frame.classList.add('is-playing');
      current = video;
    }

    /* The glitch this fixes: on desktop, mouseenter starts a muted
       preview, so by the time the visitor clicks, video.paused is
       already false. A plain paused/not-paused toggle therefore
       PAUSED the clip on the first click - it looked like the play
       button did nothing.

       The real question is not "is it playing" but "is it playing
       with sound", which is what the visitor actually asked for. */
    function toggle() {
      var playingWithSound = !video.paused && !video.muted;

      if (playingWithSound) {
        video.pause();
        frame.classList.remove('is-playing');
        if (current === video) current = null;
        return;
      }

      play(true);
    }

    frame.addEventListener('click', function (e) {
      e.preventDefault();
      toggle();
    });

    frame.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      toggle();
    });

    if (canHover && !reduced) {
      frame.addEventListener('mouseenter', function () {
        if (!video.paused) return;
        video.muted = true;
        video.play().catch(function () {});
      });

      frame.addEventListener('mouseleave', function () {
        if (current === video) return; // deliberately playing with sound
        video.pause();
        video.currentTime = 0;
      });
    }

    var btn = frame.querySelector('.pb-ugc__play');
    if (btn) {
      btn.setAttribute('tabindex', '-1'); // frame itself is the control
    }
    frame.setAttribute('tabindex', '0');
    frame.setAttribute('role', 'button');
  }

  /* Smooth-scroll the CTA back to the buy box, and stop any playing
     clip on the way - audio continuing after the visitor has left
     the section is disorienting. */
  function wireAnchors(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-pb-anchor]'), function (a) {
      if (a.dataset.pbAnchorInit === '1') return;
      a.dataset.pbAnchorInit = '1';
      a.addEventListener('click', function (e) {
        var hash = (a.getAttribute('href') || '').split('#')[1];
        if (!hash) return;
        var target = document.getElementById(hash);
        if (!target) return;
        e.preventDefault();
        stopCurrent(null);
        target.scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });
  }

  /* ---- Centre-focused carousel ------------------------------
     The clip nearest the middle of the rail is the active one. Scroll
     position is the single source of truth, so a swipe, a trackpad, a
     keyboard arrow and a tap on a side clip all end up in the same
     state without any of them knowing about each other.

     Two jobs:

     1. Size the end gutters. Without them the rail cannot scroll far
        enough for the first and last clip to reach the middle, so
        clip one would sit against the left edge instead of being the
        one on show. Measured rather than expressed in CSS because a
        percentage padding resolves against the containing block, and
        this rail is deliberately wider than its container on mobile.

     2. Mark the active card on scroll, throttled to one read per
        frame. Reading offsetLeft/clientWidth on every scroll event
        would be a forced layout per event, and scroll events fire far
        faster than frames. */
  function initCarousel(rail) {
    if (rail.dataset.pbUgcRail === '1') return;
    rail.dataset.pbUgcRail = '1';

    var cards = Array.prototype.slice.call(rail.querySelectorAll('.pb-ugc__card'));
    if (cards.length < 2) {
      rail.setAttribute('data-pb-ugc-ready', '');
      if (cards.length) cards[0].classList.add('is-active');
      return;
    }

    var ticking = false;
    var activeIndex = -1;
    var lastGutter = -1;

    /* Re-measured whenever the rail or a card changes size, not once
       on init. Measuring once raced the stylesheet: if the card had
       not yet taken its CSS width the gutter came out at a few pixels
       instead of half the rail, and clip one sat against the left
       edge - the exact thing the gutter exists to prevent.

       Whatever the rail was scrolled to stays centred afterwards,
       since changing the padding moves every card under the current
       scroll position. */
    function measure() {
      var cardW = cards[0].getBoundingClientRect().width;
      var railW = rail.clientWidth;
      if (!cardW || !railW) return;

      var gutter = Math.max(0, (railW - cardW) / 2);
      if (Math.abs(gutter - lastGutter) < 1) return;
      lastGutter = gutter;

      var keep = activeIndex > -1 ? cards[activeIndex] : null;
      rail.style.setProperty('--pb-ugc-pad', gutter + 'px');

      if (keep) centreOn(keep, false);
    }

    /* Measured from bounding rects, not offsetLeft. offsetLeft is
       relative to the nearest positioned ancestor, and the rail is not
       positioned - so it was reporting each card's distance from some
       wrapper further up, leaving the "centred" clip about 95px off.
       Rects are viewport-relative for both, so the comparison holds
       whatever the ancestors are doing. */
    function offsetFromCentre(card) {
      var r = rail.getBoundingClientRect();
      var c = card.getBoundingClientRect();
      return c.left + c.width / 2 - (r.left + r.width / 2);
    }

    function nearestToCentre() {
      var best = 0;
      var bestGap = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var gap = Math.abs(offsetFromCentre(cards[i]));
        if (gap < bestGap) { bestGap = gap; best = i; }
      }
      return best;
    }

    /* scrollTo on the rail, not scrollIntoView on the card.
       scrollIntoView walks every scrollable ancestor, so it dragged
       the page as well as the rail, and its smooth animation fought
       scroll-snap - the rail would slide and then snap back to where
       it started. */
    var canSmooth = 'scrollBehavior' in document.documentElement.style;

    function centreOn(card, smooth) {
      var target = rail.scrollLeft + offsetFromCentre(card);
      if (smooth && !reduced && canSmooth && rail.scrollTo) {
        rail.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        // Plain assignment always lands, where a smooth scroll that is
        // unsupported or frame-starved simply never arrives.
        rail.scrollLeft = target;
      }
    }

    function setActive(i) {
      if (i === activeIndex || !cards[i]) return;

      if (activeIndex > -1 && cards[activeIndex]) {
        cards[activeIndex].classList.remove('is-active');
        // A clip scrolled off centre should not keep talking.
        var prev = cards[activeIndex].querySelector('video');
        if (prev && current === prev) stopCurrent(null);
      }
      activeIndex = i;
      cards[i].classList.add('is-active');
    }

    function paint() {
      ticking = false;
      setActive(nearestToCentre());
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }

    /* Tapping a side clip brings it to the middle instead of playing
       it. Playing a clip that is two thirds off screen and scaled
       down is never what the tap meant. */
    cards.forEach(function (card) {
      card.addEventListener(
        'click',
        function (e) {
          if (card.classList.contains('is-active')) return;
          e.preventDefault();
          e.stopPropagation();
          /* Resize now, scroll after. Waiting for the scroll to settle
             before swapping the sizes left the tapped clip small for
             the whole slide, so the growth read as a delayed reaction
             rather than a response to the tap. The scroll listener
             still has the final say if the swipe ends elsewhere. */
          setActive(cards.indexOf(card));
          centreOn(card, true);
        },
        true // capture, ahead of the frame's own play handler
      );
    });

    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); onScroll(); });

    /* Catches the stylesheet landing, a web font reflowing the
       captions, and the section being re-rendered in the editor -
       none of which fire a window resize. */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { measure(); onScroll(); });
      ro.observe(rail);
      ro.observe(cards[0]);
    } else {
      window.addEventListener('load', function () { measure(); onScroll(); });
    }

    measure();
    rail.setAttribute('data-pb-ugc-ready', '');
    // scrollLeft is 0 on load, and with the gutter in place that is
    // exactly the first clip centred.
    paint();
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-pb-ugc-item]'), wire);
    Array.prototype.forEach.call(root.querySelectorAll('[data-pb-ugc-rail]'), initCarousel);
    wireAnchors(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  // Scrolling a playing clip well out of view should stop the audio.
  if ('IntersectionObserver' in window) {
    var off = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) return;
          var v = en.target.querySelector('video');
          if (v && current === v) stopCurrent(null);
        });
      },
      { threshold: 0 }
    );
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-pb-ugc-item]').forEach(function (f) {
        off.observe(f);
      });
    });
  }
})();
